/**
 * Kiosk-Isolated Firebase App
 *
 * Creates a secondary named Firebase app with browserSessionPersistence.
 * This ensures all kiosk auth operations are stored in sessionStorage (tab-scoped)
 * and NEVER bleed into the main app's localStorage — protecting admin sessions.
 *
 * Problem solved: When the kiosk calls signInWithCustomToken() on the shared
 * `auth` instance, it overwrites the admin's session in localStorage, logging
 * them out on all open tabs. This secondary app eliminates that entirely.
 *
 * Auth state storage comparison:
 *   Main app:   localStorage  → persists across tabs + across browser restarts
 *   Kiosk app:  sessionStorage → scoped to THIS tab only, cleared on tab close
 */

import { getApps, initializeApp } from 'firebase/app';
// eslint-disable-next-line no-restricted-imports -- this IS the kiosk auth adapter; direct firebase/auth import is intentional here
import {
  getAuth,
  browserSessionPersistence,
  setPersistence,
  connectAuthEmulator,
  signOut,
  signInWithCustomToken,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';

const cfg = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const KIOSK_APP_NAME = '[kiosk]';

// Reuse existing kiosk app on HMR / React StrictMode double-mount
const kioskApp =
  getApps().find((a) => a.name === KIOSK_APP_NAME) ??
  initializeApp(cfg, KIOSK_APP_NAME);

export const kioskAuth = getAuth(kioskApp);

// browserSessionPersistence: tokens stored in sessionStorage (tab-scoped).
// This is the core isolation that prevents admin-session contamination.
// Firebase queues auth operations internally, so subsequent calls respect
// this setting even if they execute before the promise resolves.
void setPersistence(kioskAuth, browserSessionPersistence);

// Mirror emulator connection if in dev mode (same port as main auth emulator)
if (import.meta.env.VITE_USE_EMULATORS === '1') {
  const host = import.meta.env.VITE_EMU_HOST || '127.0.0.1';
  const port = Number(import.meta.env.VITE_AUTH_EMULATOR_PORT || 9099);
  connectAuthEmulator(kioskAuth, `http://${host}:${port}`, { disableWarnings: true });
}

/**
 * Sign in to the kiosk Firebase app with a custom token.
 * Routes through lib/ so consumers never import firebase/auth directly.
 */
export function kioskSignInWithCustomToken(token: string): Promise<unknown> {
  return signInWithCustomToken(kioskAuth, token);
}

/**
 * Sign out of the kiosk Firebase session.
 * Only affects the kiosk tab — the admin session on other tabs is untouched.
 */
export function kioskSignOutFirebase(): Promise<void> {
  return signOut(kioskAuth);
}

/**
 * Subscribe to kiosk auth state changes.
 * Scoped to the kiosk app — does NOT receive changes from the main app's auth.
 */
export function kioskOnAuthStateChanged(
  cb: (user: User | null) => void
): () => void {
  return onAuthStateChanged(kioskAuth, cb);
}
